/**
 * @module app.shared
 */

import { I18N } from 'aurelia-i18n';
import { IFormatOptions, ILocaleOptions } from 'app/shared/lib/formatting/formatting-types';

/**
 * Get a resource format string from the localized resources.
 *
 * Examples:
 * * `getResourceFormat(i18n, 'ui:formats.date')` returns `i18n.tr('ui:formats.date.default')`
 * * `getResourceFormat(i18n, 'ui:formats.date', { format: 'long' })` returns `i18n.tr('ui:formats.date.long')`
 * * `getResourceFormat(i18n, 'ui:formats.date', { format: 'cm:formats.date.custom' })` returns `i18n.tr('cm:formats.date.custom')`
 * * `getResourceFormat(i18n, 'ui:formats.date', { format: '!M/d' })` returns `'M/d'`
 *
 * @param i18n the `I18N` instance to use to look up the resource format
 * @param resourceKeyPrefix The key prefix to use when looking up child keys
 * @param options (*optional*) formatting options such as a format string or language
 */
export function getResourceFormat(i18n: I18N, resourceKeyPrefix: string, options?: IFormatOptions): string {
    const actualFormat = options && options.format || 'default';
    if(actualFormat.startsWith('!')) {
        return actualFormat.substr(1);
    }

    // format is a resource string (starts with ns:)
    let key: string;
    if(actualFormat.match(/^[A-Za-z]+:/)) {
        key = actualFormat;
    }
    else {
        key = `${resourceKeyPrefix}.${actualFormat}`;
    }

    return i18n.tr(key, { lng: options && options.language || undefined }) || '';
}

/**
 * Get a locale name for the specified language
 *
 * @param i18n the `I18N` instance to use to look up the resource format
 * @param resourceKey The key to use when looking up the locale in the resources
 * @param options (*optional*) formatting options such as a format string or language
 */
export function getLanguageLocale(i18n: I18N, resourceKey: string, options?: ILocaleOptions): string {
    if(options && options.locale) { return options.locale; }

    const translationOptions = { lng: options && options.language || undefined };
    const localeCode = i18n.tr(resourceKey) || 'en';
    return localeCode;
}
